import Button from "@mui/material/Button";
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { isFormChanged } from "util/forms";
import { compareIds, when } from "util/support";
import { WebRoutes } from "consts/enpoints/web";
import Tabs from "ui/atoms/Tabs";
import { Merchant } from "ui/forms/ECommerce";
import { Step, withApplication } from "ui/organizms/ECommerce/Onboarding/common";
import UBOIdentityVerificationTitle from "./UBOIdentityVerificationTitle";
import useUBOIdentityVerification from "./useUBOIdentityVerification";
import { isStepCompleted, withDetectIncomplete } from "features/ecommerce/helpers";
import { useOnboardingApplication } from "../../hooks";

const UBOIdentityVerification = () => {
    const { getApplicationModel } = useOnboardingApplication();
    const {
        tab,
        uboIdentities,
        handleAddUbo,
        handleRemoveUbo,
        onIdentityChange,
        ...identityVerification
    } = useUBOIdentityVerification();

    const isDisabled = (
        !isFormChanged(identityVerification) ||
        !identityVerification.isValid ||
        !withDetectIncomplete(
            identityVerification.isIncomplete,
            identityVerification.requestedChangeFields
        )
    );

    const getRequestedChangeFieldsTabIndexes = () => {
        const { requestedChangeFields } = identityVerification;

        if (!requestedChangeFields || (typeof requestedChangeFields === 'string')) {
            return new Set();
        }

        return uboIdentities.reduce((tabIndexes, identity, index) => {
            for (const field in requestedChangeFields) {
                const [entityId] = field.split('.');

                if (compareIds(entityId, String(identity.pki))) {
                    return tabIndexes.add(index);
                }
            }

            return tabIndexes;
        }, new Set());
    };

    const getTabLabelActionProps = (index: number) => {
        const indexes = getRequestedChangeFieldsTabIndexes();


        if (indexes.has(index)) {
            return {
                startIcon: <WarningAmberIcon />,
                color: 'warning',
            };
        }

        if (!isStepCompleted(identityVerification.isIncomplete)) {
            return {
                startIcon: <DeleteForeverIcon />,
                color: 'error',
                children: 'Delete',
                onClick: handleRemoveUbo(index)
            };
        }
    };

    return (
        <Step
            getApplicationModel={getApplicationModel}
            route={WebRoutes.Onboarding.Merchant}
            title={(
                <UBOIdentityVerificationTitle
                    slotProps={{
                        action: {
                            disabled: isDisabled,
                            startIcon: <AddIcon />,
                            onClick: handleAddUbo,
                            children: `Add more UBO's`
                        }
                    }}
                >
                    UBO Identity Verification
                </UBOIdentityVerificationTitle>
            )}
        >
            <Tabs
                value={tab}
                onChange={onIdentityChange}
                model={uboIdentities.map((identity, key, identities) => ({
                    key,
                    label: (
                        <UBOIdentityVerificationTitle
                            slotProps={{
                                action: when(
                                    identities.length > 1,
                                    getTabLabelActionProps(key)
                                )
                            }}
                        >
                            {identity.fullName || 'UBO Identity'}
                        </UBOIdentityVerificationTitle>
                    ),
                    component: (
                        <Merchant.UBOIdentityVerification.Form
                            {...identityVerification}
                        >
                            <Button
                                variant='outlined'
                                disabled={isDisabled}
                                onClick={handleAddUbo}
                            >
                                Add UBO
                            </Button>
                        </Merchant.UBOIdentityVerification.Form>
                    )
                }))}
            />
        </Step>
    );
};

export default withApplication(UBOIdentityVerification);
